import React, { useState, useEffect } from 'react';
import { ChevronDown, Plus, Building2, Check, Phone } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BusinessSwitcher = ({ token, currentBusinessId, onBusinessChange }) => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingBusiness, setAddingBusiness] = useState(false);
  const [newBusiness, setNewBusiness] = useState({
    business_name: '',
    website: '',
    phone: ''
  });

  useEffect(() => {
    if (token) {
      fetchBusinesses();
    }
  }, [token]);

  const fetchBusinesses = async () => {
    try {
      const res = await axios.get(`${API}/user/businesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinesses(res.data.businesses || []);
    } catch (err) {
      console.error('Failed to fetch businesses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitch = async (businessId) => {
    if (businessId === currentBusinessId) return;
    
    try {
      await axios.post(`${API}/user/businesses/switch/${businessId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Switched business');
      if (onBusinessChange) {
        onBusinessChange(businessId);
      }
      navigate(`/business/${businessId}`);
    } catch (err) {
      toast.error('Failed to switch business');
    }
  };

  const handleAddBusiness = async (e) => {
    e.preventDefault();
    if (!newBusiness.business_name.trim()) {
      toast.error('Business name is required');
      return;
    }

    setAddingBusiness(true);
    try {
      const res = await axios.post(`${API}/user/businesses/add`, newBusiness, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(res.data.message);
      setShowAddModal(false);
      setNewBusiness({ business_name: '', website: '', phone: '' });
      fetchBusinesses();
      
      // Navigate to new business dashboard
      navigate(`/business/${res.data.business.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add business');
    } finally {
      setAddingBusiness(false);
    }
  };

  const currentBusiness = businesses.find(b => b.id === currentBusinessId);

  if (loading || businesses.length === 0) {
    return null;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="gap-2 max-w-[200px]" data-testid="business-switcher">
            <Building2 className="w-4 h-4 shrink-0" />
            <span className="truncate">{currentBusiness?.business_name || 'Select Business'}</span>
            <ChevronDown className="w-4 h-4 shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[240px]">
          <div className="px-2 py-1.5 text-xs text-muted-foreground font-medium">
            Your Businesses
          </div>
          {businesses.map((business) => (
            <DropdownMenuItem
              key={business.id}
              onClick={() => handleSwitch(business.id)}
              className="cursor-pointer"
              data-testid={`business-option-${business.id}`}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 className="w-4 h-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{business.business_name}</p>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      {business.retell_phone_number ? (
                        <>
                          <Phone className="w-3 h-3" />
                          <span>{business.retell_phone_number}</span>
                        </>
                      ) : (
                        <span className="text-yellow-500">Setup needed</span>
                      )}
                    </div>
                  </div>
                </div>
                {business.id === currentBusinessId && (
                  <Check className="w-4 h-4 text-primary shrink-0" />
                )}
              </div>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setShowAddModal(true)}
            className="cursor-pointer text-primary"
            data-testid="add-business-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Another Business
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Add Business Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Business</DialogTitle>
            <DialogDescription>
              Each business has its own phone number, subscription, and settings.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddBusiness} className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">
                Business Name *
              </label>
              <Input
                value={newBusiness.business_name}
                onChange={(e) => setNewBusiness({ ...newBusiness, business_name: e.target.value })}
                placeholder="My New Business"
                data-testid="new-business-name"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">
                Website
              </label>
              <Input
                value={newBusiness.website}
                onChange={(e) => setNewBusiness({ ...newBusiness, website: e.target.value })}
                placeholder="mybusiness.com"
              />
              <p className="text-xs text-muted-foreground mt-1">
                We'll use this to train your Pro
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">
                Phone Number
              </label>
              <Input
                value={newBusiness.phone}
                onChange={(e) => setNewBusiness({ ...newBusiness, phone: e.target.value })}
                placeholder="(555) 123-4567"
              />
            </div>

            <div className="bg-muted/50 rounded-lg p-3 text-sm">
              <p className="text-muted-foreground">
                <strong className="text-foreground">Note:</strong> Each business requires its own subscription and phone number purchase.
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={addingBusiness}
                className="flex-1"
              >
                {addingBusiness ? 'Adding...' : 'Add Business'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BusinessSwitcher;
